<template>
  <vs-wrapper>
    <project-creation-form
      ref="creationForm"
      :input="createdProjectName"
      @save="onProjectCreation"
      @input-change="onProjectCreateInputChange"
    />

    <v-divider class="project-divider" aria-label="or" />

    <projects-list
      :is-loading="isLoadingProjects"
      :has-loading-error="isProjectLoadError"
      :projects="projectData"
      :owner-id="ownerId"
      :current-index="page"
      :search-input="searchInput"
      :selected-project-id="selectedProjectId"
      @select="onProjectSelection"
      @search="onSearchInputChange"
      @page-change="onPageChange"
    />
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect, inject, Ref } from 'vue'
import ProjectsList from './ProjectsList.vue'
import { useGetProjectsQuery } from '../../api/useProjectsQueries'
import {
  addJobsToProjectMutation,
  addJobToProjectMutation,
  createProjectMutation,
} from '../../api/useProjectsMutations'
import VsWrapper from '../vision/VsWrapper.vue'
import ProjectCreationForm from './ProjectCreationForm.vue'
import { ProjectCreationComponent } from './ProjectCreationComponent'
import { ProjectSelectionMessage } from './ProjectSelectionMessage'
import { AxiosResponse } from 'axios'

// ---------------------------
//  Props
// ---------------------------

const props = defineProps<{
  jobId?: number
  jobIds?: string[]
  isBulkUpdate?: boolean
  requireUserConfirmation: boolean
}>()

// ---------------------------
//  Events
// ---------------------------

const emit = defineEmits([
  'project-loading-error',
  'project-selected',
  'project-created',
  'job-added-to-project',
])

// ---------------------------
//  Refs
// ---------------------------

const currentPage: Ref<number> = ref(1)
const searchInput: Ref<string> = ref('')
const createdProjectName: Ref<string> = ref('')
const currentLimit: Ref<number> = ref(8)

const queryGenerator = computed(() => ({
  offset: (currentPage.value - 1) * currentLimit.value,
  limit: currentLimit.value,
  query: searchInput.value,
}))

const selectedProjectId: Ref<string> = ref('')
const creationForm: Ref<ProjectCreationComponent | null> = ref(null)
const userConfirmation: Ref<boolean> = inject('userConfirmation', ref(false))
const ownerId: Ref<string> = inject('ownerId', ref(''))

// ---------------------------
//  Composables
// ---------------------------

// Mutators
const { mutate: projectCreateMutate } = createProjectMutation()
const { mutate: mutateAddJobs } = addJobsToProjectMutation()
const { mutate: mutateAddJob } = addJobToProjectMutation()

const {
  isLoading: isLoadingProjects,
  data: projectData,
  isError: isProjectLoadError,
} = useGetProjectsQuery(queryGenerator)

// ---------------------------
//  Computed
// ---------------------------

const requiresUserConfirmation = computed(() => props.requireUserConfirmation)
const hasUserConfirmed = computed(() => userConfirmation.value)
const page = computed({
  get() {
    return currentPage.value
  },
  set(value) {
    currentPage.value = value
  },
})
// ---------------------------
//  Watchers
// ---------------------------

watch(isProjectLoadError, () => {
  emit('project-loading-error')
})

watchEffect(async () => {
  // Starts the project creation  when the creation form
  // component is in a "saving" state.
  if (creationForm.value?.isSaving) {
    onProjectCreation()
    creationForm.value?.resetForm()
  }
})

watch(userConfirmation, async () => {
  // Initiate add job to project when user has confirmed saving
  // (click the "Update job(s) button").
  if (requiresUserConfirmation.value && userConfirmation.value) {
    if (selectedProjectId.value === '') return

    onProjectSelection(selectedProjectId.value)
  }
})

// ---------------------------
//  Methods
// ---------------------------

const resetPage = () => {
  currentPage.value = 1
}

// ---------------------------
//  Event
// ---------------------------

const onProjectCreateInputChange = async (value: string) => {
  createdProjectName.value = value
}

const onSearchInputChange = async (value: string) => {
  if (searchInput.value === '') {
    resetPage()
  }

  searchInput.value = value
}

const onProjectCreation = async () => {
  if (!createdProjectName.value) return

  createProject(createdProjectName.value)
  resetPage()
}

const onPageChange = (value: number) => {
  currentPage.value = value
}

const onProjectSelection = async (projectId: string) => {
  selectedProjectId.value = projectId
  emit('project-selected', projectId)

  // If user confirmation is required, then the user must confirm
  // that they want to proceed before calling the mutations.
  if (requiresUserConfirmation.value && !hasUserConfirmed.value) return

  // NOTE: Users are able to select only 1 job when performing the bulk
  // action so cannot just check for length > 1.
  props.isBulkUpdate ? bulkAddJobsToProject(projectId) : addJobToProject(projectId)
}

// ---------------------------
//  Methods
// ---------------------------

const createProject = (projectName: string) => {
  projectCreateMutate(
    {
      name: projectName,
      ownerId: (ownerId as Ref<string>).value,
    },
    {
      onError: () => {
        emit('project-created', {
          type: 'error',
          message: 'Failed to create project, please try again',
        } as ProjectSelectionMessage)
      },
      onSuccess: (result: AxiosResponse) => {
        emit('project-created', {
          type: 'info',
          projectId: result.data.id as string,
          message: 'Project created',
        } as ProjectSelectionMessage)

        onProjectSelection(result.data.id as string)
      },
    },
  )
}

/**
 * @param {string} projectId - The Project Id.
 */
const addJobToProject = (projectId: string) => {
  if (props.jobId === null) return

  mutateAddJob(
    {
      projectId,
      jobId: props.jobId as number,
    },
    {
      onError: () => {
        emit('job-added-to-project', {
          type: 'error',
          projectId,
          message: 'Failed to add job to project',
        } as ProjectSelectionMessage)
      },
      onSuccess: () => {
        emit('job-added-to-project', {
          type: 'success',
          projectId,
          message: 'Job added to project',
        } as ProjectSelectionMessage)
      },
    },
  )
}

const bulkAddJobsToProject = (projectId: string) => {
  if (props.jobIds === null) return

  mutateAddJobs(
    {
      projectId,
      jobIds: props.jobIds as string[],
    },
    {
      onError: () => {
        emit('job-added-to-project', {
          type: 'error',
          project_id: projectId,
          message: 'Failed to add jobs to project',
        })
      },
      onSuccess: () => {
        emit('job-added-to-project', {
          type: 'info',
          project_id: projectId,
          message: 'Project updated',
        })
      },
    },
  )
}
</script>

<style scoped>
.project-divider::after {
  content: attr(aria-label);
  font-family: var(--font-regular), serif;
  font-size: 12px;

  text-transform: uppercase;
  position: absolute;

  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0 var(--space-smaller);
  background-color: white;
}
.project-divider {
  position: relative;
  margin: var(--space-large) 0 0 0;
  padding: 0 0 var(--space-large) 0;
}
</style>
